<template>
    <div class="va-table-wrapper">
        <h2>User Panel</h2>

        <div class="va-user-table-filter">
            <el-button icon="el-icon-user" type="primary" @click="handleCreate()"> New User </el-button>

            <el-input v-model="searchForm.text" class="va-search-field" clearable placeholder="Suchtext" prefix-icon="el-icon-search" size="medium">
            </el-input>

            <el-select v-model="searchForm.role" clearable placeholder="Rolle" size="medium">
                <el-option v-for="item in roleOptions" :key="item.value" :label="item.label" :value="item.value"> </el-option>
            </el-select>

            <el-select v-model="searchForm.isBlocked" clearable placeholder="Status" size="medium">
                <el-option v-for="item in blockedOptions" :key="item.value" :label="item.label" :value="item.value"> </el-option>
            </el-select>
        </div>

        <el-table
            ref="userTable"
            :data="tableData"
            :default-sort="searchForm.sortingParams"
            :row-key="tableData.id"
            class="va-module-table"
            height="550px"
            stripe
            style="width: 100%"
            @sort-change="setSortingParams"
        >
            <!--only dynamic with colum-->
            <el-table-column :sort-orders="allowedSorting" label="ID" prop="id" sortable="custom" width="60px"> </el-table-column>

            <el-table-column :sort-orders="allowedSorting" label="Username" prop="username" sortable="custom"> </el-table-column>

            <el-table-column :sort-orders="allowedSorting" label="Vorname" prop="firstname" sortable="custom"> </el-table-column>

            <el-table-column :sort-orders="allowedSorting" label="Nachname" prop="lastname" sortable="custom"> </el-table-column>

            <el-table-column :sort-orders="allowedSorting" label="Emails" prop="emails" :sortable="false" width="400px">
                <template slot-scope="scope">
                    <el-tag v-for="email in scope.row.emails.split(',')" v-if="scope.row.emails.length > 0" size="small" class="va-table-tags">
                        {{ email }}
                    </el-tag>
                </template>
            </el-table-column>

            <el-table-column :sort-orders="allowedSorting" label="Mobil Aktivität" prop="last_activity" sortable="custom" width="140px">
                <template slot-scope="scope">
                    <div v-if="scope.row.isOnline" class="activity online">
                        <span class="dot"></span>
                        <span>Online</span>
                    </div>
                    <div v-else class="activity offline">
                        <span class="dot"></span>
                        <span v-if="scope.row.lastActivity">
                            {{ datePickerHelper.prettyDate(scope.row.lastActivity) }}
                        </span>
                        <span v-else>Never</span>
                    </div>
                </template>
            </el-table-column>

            <template>
                <el-table-column :sort-orders="allowedSorting" label="Platform" prop="device_type" sortable="custom" width="100px">
                    <template slot-scope="scope">
                        <div class="icon-cell">
                            <IOSIcon v-if="scope.row.deviceType === 'ios'" color="#aaa" size="24" class="icon" />
                            <AndroidIcon v-else-if="scope.row.deviceType === 'android'" color="#aaa" size="24" class="icon" />
                        </div>
                    </template>
                </el-table-column>
            </template>

            <el-table-column :sort-orders="allowedSorting" label="Rolle" prop="role" sortable="custom" width="110px">
                <template slot-scope="scope">
                    {{ selectOptionHelper.userRoleTypeConverter(scope.row.role) }}
                </template>
            </el-table-column>

            <el-table-column :sort-orders="allowedSorting" label="Aktiv" prop="status" sortable="custom" width="80px">
                <template slot-scope="scope">
                    <el-tag :type="scope.row.status === 'permitted' ? 'success' : 'danger'" style="font-size: 17px">
                        <i :class="scope.row.status === 'permitted' ? 'el-icon-check' : 'el-icon-minus'"></i>
                    </el-tag>
                </template>
            </el-table-column>

            <el-table-column :sort-orders="allowedSorting" label="Admin" prop="admin" sortable="custom" width="90px">
                <template slot-scope="scope">
                    <el-tag :type="scope.row.admin === true ? 'success' : 'danger'" style="font-size: 17px">
                        <i :class="scope.row.admin === true ? 'el-icon-check' : 'el-icon-minus'"></i>
                    </el-tag>
                </template>
            </el-table-column>

            <el-table-column :sort-orders="allowedSorting" label="Aktionen" :sortable="false" width="170px">
                <template slot-scope="scope">
                    <el-button size="small" type="primary" icon="el-icon-edit" circle @click="handleEdit(scope.row)"> </el-button>
                    <el-button
                        v-if="scope.row.id !== auth.user.profile.id"
                        size="small"
                        :type="scope.row.status === 'blocked' ? 'primary' : 'danger'"
                        :icon="scope.row.status === 'blocked' ? 'el-icon-block-user' : 'el-icon-unblock-user'"
                        circle
                        @click="handleBlock(scope.row)"
                    >
                    </el-button>
                    <el-button
                        v-if="scope.row.id !== auth.user.profile.id"
                        size="small"
                        type="danger"
                        icon="el-icon-delete"
                        circle
                        @click="handleDelete(scope.row)"
                    >
                    </el-button>
                </template>
            </el-table-column>
        </el-table>

        <div class="va-table-footer">Zeige {{ tableData.length }} von {{ totalCount }} Einträgen</div>

        <el-pagination
            background
            :current-page="currentPage"
            :page-size="perPage"
            :page-sizes="[10, 20, 50, 100]"
            :total="totalCount"
            layout="sizes, prev, pager, next, jumper"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
        >
        </el-pagination>

        <el-dialog
            v-if="dialogVisible"
            :visible.sync="dialogVisible"
            :title="'User ' + (isUserCreation ? 'erstellen' : userForm.id + ' bearbeiten')"
            width="960px"
            custom-class="dialog"
        >
            <div class="va-dialog-split-container">
                <el-form :model="userForm" :rules="userRules" label-width="90px" ref="userForm" class="va-module-form">
                    <div class="va-dialog-container-left">
                        <el-form-item label="Username" prop="username">
                            <el-input v-model="userForm.username" placeholder="Username eingeben"> </el-input>
                        </el-form-item>

                        <el-form-item label="Vorname" prop="firstname">
                            <el-input v-model="userForm.firstname" placeholder="Vorname eingeben"> </el-input>
                        </el-form-item>

                        <el-form-item label="Nachname" prop="lastname">
                            <el-input v-model="userForm.lastname" placeholder="Nachname eingeben"> </el-input>
                        </el-form-item>

                        <el-form-item label="Emails" prop="emails">
                            <el-select
                                v-model="userForm.emails"
                                class="va-multiselect-dropdown"
                                multiple
                                filterable
                                allow-create
                                default-first-option
                                @change="convertToLowerCase"
                                placeholder="Email Adressen eingeben"
                            >
                                <el-option v-for="item in allEmailOptions" :key="item.value" :label="item.label" :value="item.value"> </el-option>
                            </el-select>
                        </el-form-item>
                    </div>

                    <div class="va-dialog-container-right">
                        <div class="va-dialog-danger-box">
                            <el-form-item label="Passwort" prop="password" :rules="isUserCreation ? userRules.newPassword : userRules.editPassword">
                                <el-input v-model="userForm.password" placeholder="Neues Passwort"> </el-input>
                                <el-button size="small" type="primary" icon="el-icon-refresh" @click="generateNewPassword(14)"> </el-button>
                                <el-button size="small" type="primary" icon="el-icon-copy-document" @click="copyPassword()"> </el-button>
                            </el-form-item>
                        </div>

                        <div class="va-dialog-danger-box">
                            <el-switch v-model="userForm.isAdmin" :active-text="'User ist ' + (userForm.isAdmin ? '' : ' kein ') + 'Administrator'">
                            </el-switch>
                        </div>

                        <el-form-item label="User-Rolle" prop="role">
                            <el-select v-model="userForm.role" placeholder="Rolle angeben">
                                <el-option v-for="item in roleOptions" :key="item.value" :label="item.label" :value="item.value"> </el-option>
                            </el-select>
                        </el-form-item>
                    </div>
                </el-form>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="dialogVisible = false">Abbrechen</el-button>
                <el-button type="primary" :loading="isButtonLoading" @click="createUser()">Speichern</el-button>
            </span>
        </el-dialog>

        <el-dialog :visible.sync="showSendDialog" center title="Passwort senden" width="350px" custom-class="dialog" :close-on-click-modal="false" @close="resetSendPasswordDialog()">
            <div v-if="rowToSend" class="va-delete-dialog-body">
                <template v-if="!showEmailChoices">
                    Das Passwort an <b>{{ targetEmail }}</b> senden?
                    <el-button
                        v-if="rowToSend.emails.length > 1"
                        type="text"
                        @click="showEmailChoices = true"
                    >
                        An andere E-Mail-Adresse senden
                    </el-button>
                </template>
                <el-radio-group v-else v-model="targetEmail">
                    <el-radio v-for="email in rowToSend.emails" :key="email" :label="email">
                        {{ email }}
                    </el-radio>
                </el-radio-group>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="resetSendPasswordDialog()">Abbrechen</el-button>
                <el-button type="primary" :disabled="!targetEmail" @click="sendPassword(targetEmail)">Senden</el-button>
            </span>
        </el-dialog>

        <el-dialog :visible.sync="deleteDialogVisible" center title="Warning" width="350px" custom-class="dialog">
            <div v-if="this.rowToDelete" class="va-delete-dialog-body">
                Den Benutzer <b>{{ this.rowToDelete.username }}</b> wirklich löschen?
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="deleteDialogVisible = false">Abbrechen</el-button>
                <el-button type="danger" @click="deleteUser()">Löschen</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import auth from '../../auth.js'
import Vue from '../../main'
import selectOptionHelper from '../../Helpers/SelectOptionHelper.js'
import validation from '../../Helpers/ValidationHelper.js'
import notification from '../../Helpers/NotificationHelper'
import datePickerHelper from '../../Helpers/DatePickerHelper'
import IOSIcon from '../icons/IOS.vue'
import AndroidIcon from '../icons/Android.vue'

export default {
    components: {
        IOSIcon,
        AndroidIcon
    },
    data() {
        return {
            auth: auth,
            selectOptionHelper: selectOptionHelper,
            datePickerHelper: datePickerHelper,

            submitDelay: 400, //ms
            submitTimer: null,

            currentPage: 1,
            perPage: 10,
            tableData: [],
            totalCount: 0, //for display of total count
            allowedSorting: ['ascending', 'descending'],

            roleOptions: [
                {
                    value: 'user',
                    label: 'User'
                },
                {
                    value: 'task_creator',
                    label: 'TaskCreator'
                }
            ],

            blockedOptions: [
                {
                    value: 'blocked',
                    label: 'gesperrt'
                },
                {
                    value: 'permitted',
                    label: 'aktiv'
                }
            ],

            searchForm: {
                text: '',
                role: '',
                isBlocked: '',
                sortingParams: {
                    prop: 'id',
                    order: 'ascending'
                }
            },

            dialogVisible: false,
            showSendDialog: false,
            showEmailChoices: false,
            targetEmail: '',
            rowToSend: null,
            isButtonLoading: false,
            isUserCreation: false,
            userForm: {
                id: null,
                username: '',
                firstname: '',
                lastname: '',
                emails: [],
                password: '',
                isAdmin: false,
                role: 'user',
                status: 'permitted'
            },

            rowToDelete: null,
            deleteDialogVisible: false,

            allEmailOptions: [],

            userRules: {
                username: [validation.getRequiredValidationObject('change'), validation.getMinMaxValidationObject(2, 50, 'change')],
                firstname: [validation.getRequiredValidationObject('change'), validation.getMinMaxValidationObject(2, 50, 'change')],
                lastname: [validation.getRequiredValidationObject('change'), validation.getMinMaxValidationObject(2, 50, 'change')],
                emails: [validation.getRequiredValidationObject('change'), validation.getEmailArrayValidationObject('change')],
                newPassword: [validation.getRequiredValidationObject('change'), validation.getMinMaxValidationObject(6, 50, 'change')],
                editPassword: [validation.getMinMaxValidationObject(6, 50, 'change')]
            }
        }
    },

    methods: {
        setSortingParams(column, prop, order) {
            this.searchForm.sortingParams.prop = column.prop
            this.searchForm.sortingParams.order = column.order
        },

        handleEdit(row) {
            let data = JSON.parse(JSON.stringify(row))

            //reset user form
            this.userForm.id = data.id
            this.userForm.username = data.username
            this.userForm.firstname = data.firstname
            this.userForm.lastname = data.lastname
            this.userForm.emails = data.emails ? data.emails.split(',') : []
            this.userForm.password = ''
            this.userForm.isAdmin = data.admin
            this.userForm.role = data.role
            this.userForm.status = data.status

            //set variable isUserCreation
            this.isUserCreation = false

            //open dialog modal
            this.dialogVisible = true
        },

        handleCreate() {
            //reset user form
            this.userForm.id = null
            this.userForm.username = ''
            this.userForm.firstname = ''
            this.userForm.lastname = ''
            this.userForm.emails = []
            this.userForm.isAdmin = false
            this.userForm.role = 'user'
            this.userForm.status = 'permitted'

            //generate new password
            this.generateNewPassword(14)

            //set variable isUserCreation
            this.isUserCreation = true

            //open dialog
            this.dialogVisible = true
        },

        handleDelete(row) {
            // open delete dialog
            this.deleteDialogVisible = true
            this.rowToDelete = row
        },

        deleteUser() {
            let row = this.rowToDelete
            if (row) {
                Vue.http
                    .post(
                        //update or create? decide based on id field
                        'api/v1/user/delete',
                        {
                            id: row.id
                        }
                    )
                    .then(
                        (response) => {
                            //remove deleted row from tableDate without reloading
                            let index = this.tableData.indexOf(row)
                            this.tableData.splice(index, 1)

                            //reduce totalCount in table footer by one
                            this.totalCount -= 1

                            //show success notification + close dialog
                            notification.showSuccess(this, 'User wurden gelöscht')
                            this.deleteDialogVisible = false
                        },
                        (response) => {
                            notification.showError(this, 'User konnte nicht gelöscht werden')
                            this.deleteDialogVisible = false
                        }
                    )
            }
        },

        handleBlock(row) {
            //define isBlocked bool
            let isBlocked = row.status === 'blocked'

            Vue.http
                .post(
                    //update or create? decide based on id field
                    'api/v1/user/toggle-status',
                    {
                        id: row.id
                    }
                )
                .then(
                    (response) => {
                        //invert isBlocked status
                        isBlocked = !isBlocked

                        //update table row with new status
                        row.status = isBlocked ? 'blocked' : 'permitted'

                        notification.showSuccess(this, 'User wurden ' + (isBlocked ? 'deaktiviert' : 'aktiviert'))
                    },
                    (response) => {
                        notification.showError(this, 'Daten konnten nicht gespeichert werden')
                    }
                )
        },

        convertToLowerCase() {
            //convert all input emails to lower case and remove duplicates
            let lowerCaseArray = this.userForm.emails.map((email) => email.toLowerCase())
            this.userForm.emails = [...new Set(lowerCaseArray)]
        },

        copyPassword() {
            navigator.clipboard.writeText(this.userForm.password)
        },

        generateNewPassword(length) {
            //define variables
            let alphabet = '23456789abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ!@#$%^&*()-_=+',
                result = ''
            const chars = new Uint32Array(length)
            crypto.getRandomValues(chars) //use crypto for better randomness
            
            //generate new password and set it to userForm.password
            this.userForm.password = Array.from(chars, n => alphabet[n % alphabet.length]).join('')
        },

        createUser() {
            this.$refs['userForm'].validate((valid) => {
                //if form is valid, then submit data
                if (valid) {
                    //start loading button animation
                    this.isButtonLoading = true

                    this.rowToSend = this.userForm
                    Vue.http
                        .post(
                            //update or create? decide based on id field
                            this.userForm.id ? 'api/v1/user/update' : 'api/v1/user/create',
                            {
                                id: this.userForm.id,
                                username: this.userForm.username,
                                firstname: this.userForm.firstname,
                                lastname: this.userForm.lastname,
                                emails: this.userForm.emails,
                                password: this.userForm.password,
                                admin: this.userForm.isAdmin,
                                role: this.userForm.role,
                                status: this.userForm.status
                            }
                        )
                        .then(
                            (response) => {
                                //show success msg
                                notification.showSuccess(this, 'Daten wurden erfolgreich gespeichert')

                                //stop loading
                                this.isButtonLoading = false

                                //close dialog
                                this.dialogVisible = false

                                if (this.userForm.id && this.userForm.password) {
                                    this.showSendDialog = true
                                    this.showEmailChoices = false
                                    this.targetEmail = this.userForm.emails[0]
                                } else if (!this.userForm.id) {
                                    this.showSendDialog = true
                                    this.showEmailChoices = false
                                    this.targetEmail = this.userForm.emails[0]
                                }

                                //update table based on updated or create request
                                if (this.userForm.id) {
                                    //find tableData row by id
                                    let index = null
                                    for (let i in this.tableData) {
                                        if (this.tableData[i].id === this.userForm.id) {
                                            index = i
                                            break
                                        }
                                    }
                                    //update if exists
                                    if (index) {
                                        this.tableData[index].username = this.userForm.username
                                        this.tableData[index].firstname = this.userForm.firstname
                                        this.tableData[index].lastname = this.userForm.lastname
                                        this.tableData[index].emails = this.userForm.emails.join(',')
                                        this.tableData[index].admin = this.userForm.isAdmin
                                        this.tableData[index].role = this.userForm.role
                                    }
                                } else {
                                    //reload table with current filter + sort settings
                                    this.resetTableLoader() //reset values on each form filter change
                                    this.getAllUsers()
                                }
                            },
                            (error) => {
                                notification.showError(this, error.body)

                                //stop loading
                                this.isButtonLoading = false
                            }
                        )
                }
            })
        },

        resetSendPasswordDialog() {
            this.showSendDialog = false
            this.showEmailChoices = false
            this.targetEmail = ''
            this.rowToSend = null
        },

        sendPassword(email) {
            Vue.http
                .post('api/v1/user/send-password', {
                    email: email,
                    password: this.rowToSend.password
                })
                .then(
                    (response) => {
                        notification.showSuccess(this, 'Passwort wurde an ' + email + ' gesendet')
                        this.showSendDialog = false
                        this.showEmailChoices = false
                        this.targetEmail = ''
                    },
                    (error) => {
                        notification.showError(this, error.body)
                    }
                )
        },

        getAllEmails() {
            Vue.http.get('api/v1/user/get-all-emails').then(
                (response) => {
                    this.allEmailOptions = response.data.map((item) => {
                        return { value: item.email, label: item.email }
                    })
                },
                (response) => {
                    //notification.showError(this, 'Daten konnten nicht geladen werden');
                }
            )
        },

        getAllUsers() {
            Vue.http
                .get('api/v1/user/get-all', {
                    params: {
                        text: this.searchForm.text,
                        role: this.searchForm.role,
                        isBlocked: this.searchForm.isBlocked,
                        column: this.searchForm.sortingParams.prop,
                        order: this.searchForm.sortingParams.order === 'ascending' ? 'asc' : 'desc',
                        page: this.currentPage,
                        perPage: this.perPage
                    }
                })
                .then(
                    (response) => {
                        // Set entries counter and current page data
                        this.totalCount = response.data.totalCount
                        this.tableData = response.data.items || []
                    },
                    (response) => {
                        // this.showError(response.data.message);
                    }
                )
        },

        handleCurrentChange(page) {
            this.currentPage = page
            this.getAllUsers()
        },

        handleSizeChange(size) {
            this.perPage = size
            this.resetTableLoader()
            this.getAllUsers()
        },

        resetTableLoader() {
            this.currentPage = 1 //reset page on filter change
            this.tableData = [] //reset tableData as well
        },

        sendDelayedSearchRequest() {
            //if timer exists: clear
            if (this.submitTimer) {
                clearTimeout(this.submitTimer)
                this.submitTimer = null
            }
            this.submitTimer = setTimeout(() => {
                this.getAllUsers()
            }, this.submitDelay)
        }
    },

    watch: {
        searchForm: {
            handler(newValue, oldValue) {
                this.resetTableLoader() //reset values on each form filter change
                this.sendDelayedSearchRequest()
            },
            deep: true
        }
    },

    beforeMount() {
        //get all users in selected service
        this.getAllUsers()

        //get all emails for autocomplete dropdown in user edit
        this.getAllEmails()
    },

    created() {},

    mounted() {},

    beforeDestroy() {}
}
</script>
